'use client';

/**
 * Structural side panel for the deck editor (non-blocks slide types).
 *
 * Navigation: 'slide' view shows slide controls + a clickable bg thumbnail.
 * Clicking the thumbnail drills into 'background' view (inline asset picker).
 */

import { useEffect, useState } from 'react';
import { convertTypedSlideToBlockSlide } from '@/lib/deck/convert-typed-to-blocks';
import type {
  CardsSlide,
  PricingSlide,
  PricingTier,
  ProblemItem,
  ProblemsSlide,
  Slide,
  SlideBg,
  TranscriptMessage,
  TranscriptSlide,
  TwoColumnSlide,
} from '@/lib/deck/types';
import { BackgroundAdjustControls } from './BackgroundAdjustControls';
import { PhotoslopLibraryPanel } from './PhotoslopLibraryPanel';
import { GeneratePanel } from './GeneratePanel';

type PanelView = 'slide' | 'background';
type BgTab = 'library' | 'upload' | 'generate';

interface SidePanelProps {
  slide: Slide | null;
  onChange: (slide: Slide) => void;
  onPickBackground: (src: string) => void;
  onDeleteSlide: () => void;
  onDuplicateSlide: () => void;
  /** Increment to open the Background picker from the canvas (e.g. image click). */
  openBackgroundNonce?: number;
}

const FIELD_LABEL = 'font-dank-mono text-[10px] uppercase tracking-widest text-slate-400';
const BUTTON =
  'rounded-full border border-beige px-3 py-1 font-inter text-xs text-slate-600 hover:border-marine-green hover:text-marine-green';
const DANGER =
  'rounded-full border border-red-200 px-3 py-1 font-inter text-xs text-red-500 hover:bg-red-50';
const SECTION = 'rounded-xl border border-beige bg-white p-4 space-y-3';

export function SidePanel({
  slide,
  onChange,
  onPickBackground,
  onDeleteSlide,
  onDuplicateSlide,
  openBackgroundNonce = 0,
}: SidePanelProps) {
  const [view, setView] = useState<PanelView>('slide');
  const [bgTab, setBgTab] = useState<BgTab>('library');

  useEffect(() => {
    if (openBackgroundNonce > 0) {
      setView('background');
    }
  }, [openBackgroundNonce]);

  if (!slide) {
    return (
      <div className="flex h-full items-center justify-center p-6 text-center text-slate-400">
        <p className="font-inter text-sm">Select a slide to edit its structure.</p>
      </div>
    );
  }

  const patchBg = (patch: Partial<SlideBg>) => {
    onChange({ ...slide, bg: { ...(slide.bg ?? {}), ...patch } } as Slide);
  };

  const pickBg = (src: string) => {
    onPickBackground(src);
    setView('slide');
  };

  // ── Background view ──────────────────────────────────────────────────────
  if (view === 'background') {
    return (
      <div className="flex h-full flex-col overflow-hidden">
        <div className="flex items-center gap-2 border-b border-beige bg-white px-4 py-3">
          <button
            type="button"
            onClick={() => setView('slide')}
            className="font-dank-mono text-[10px] uppercase tracking-widest text-slate-400 hover:text-marine-green"
          >
            ← Slide
          </button>
          <span className="font-dank-mono text-[10px] uppercase tracking-widest text-slate-300">/</span>
          <span className="font-dank-mono text-[10px] uppercase tracking-widest text-slate-700">
            Background
          </span>
        </div>

        <div className="flex gap-1 border-b border-beige bg-cream-alt px-3 py-2">
          {(['library', 'upload', 'generate'] as BgTab[]).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setBgTab(t)}
              className={`flex-1 rounded-lg px-2 py-1.5 font-inter text-xs font-semibold transition ${
                bgTab === t
                  ? 'bg-marine-green text-white shadow-sm'
                  : 'text-slate-500 hover:bg-white'
              }`}
            >
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {bgTab === 'library' && (
            <div className="p-3">
              <PhotoslopLibraryPanel active onPick={pickBg} showUploadButton={false} />
            </div>
          )}
          {bgTab === 'upload' && <SidePanelUploadTab onPick={pickBg} />}
          {bgTab === 'generate' && (
            <div className="p-3">
              <GeneratePanel onPick={pickBg} onError={(msg) => alert(msg)} />
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Slide view ────────────────────────────────────────────────────────────
  return (
    <div className="flex h-full flex-col gap-3 overflow-y-auto p-4">
      <div className={SECTION}>
        <div className="flex items-center justify-between">
          <p className={FIELD_LABEL}>Slide · {slide.type}</p>
          <div className="flex gap-2">
            <button type="button" onClick={onDuplicateSlide} className={BUTTON}>
              Duplicate
            </button>
            <button type="button" onClick={onDeleteSlide} className={DANGER}>
              Delete
            </button>
          </div>
        </div>
      </div>

      {slide.type !== 'blocks' && (
        <div className={SECTION}>
          <p className={FIELD_LABEL}>Block canvas</p>
          <p className="font-inter text-xs text-slate-600">
            Convert to draggable blocks, snap-to-anchor layout, and the new background picker.
          </p>
          <button
            type="button"
            className={BUTTON}
            onClick={() => onChange(convertTypedSlideToBlockSlide(slide))}
          >
            Convert to blocks
          </button>
        </div>
      )}

      {/* Background — clickable thumbnail */}
      <div className="space-y-2 rounded-xl border border-beige bg-white p-4">
        <p className={FIELD_LABEL}>Background</p>
        <button
          type="button"
          onClick={() => setView('background')}
          className="group relative w-full overflow-hidden rounded-lg border border-beige transition hover:border-marine-green"
        >
          {slide.bg?.src ? (
            <img src={slide.bg.src} alt="" className="aspect-[16/9] w-full object-cover" />
          ) : (
            <div
              className="flex aspect-[16/9] w-full items-center justify-center font-inter text-[10px] text-slate-400"
              style={{ backgroundColor: slide.bg?.color ?? '#0D0D14' }}
            >
              {slide.bg?.color ?? 'No image'}
            </div>
          )}
          <div className="absolute inset-0 flex items-center justify-center bg-black/0 transition group-hover:bg-black/30">
            <span className="rounded-full bg-white px-3 py-1 font-inter text-xs font-semibold text-slate-800 opacity-0 shadow transition group-hover:opacity-100">
              Change image
            </span>
          </div>
        </button>

        <label className="font-inter text-xs text-slate-600">
          <span className={FIELD_LABEL}>Fill color (hex)</span>
          <input
            type="text"
            value={slide.bg?.color ?? ''}
            onChange={(e) => patchBg({ color: e.target.value || undefined })}
            placeholder="#1C3D3D"
            className="mt-1 w-full rounded-md border border-beige bg-white px-2 py-1 font-dank-mono text-xs"
          />
        </label>
      </div>

      {slide.bg?.src && (
        <BackgroundAdjustControls
          bg={slide.bg}
          onChange={(bg) => onChange({ ...slide, bg } as Slide)}
        />
      )}

      {(slide.type === 'cover' ||
        slide.type === 'right-column' ||
        slide.type === 'cards' ||
        slide.type === 'qr' ||
        slide.type === 'problems' ||
        slide.type === 'pricing' ||
        slide.type === 'two-column' ||
        slide.type === 'ask' ||
        slide.type === 'transcript') && (
        <div className={SECTION}>
          <p className={FIELD_LABEL}>Text color</p>
          <div className="flex gap-2">
            {(['light', 'dark'] as const).map((c) => {
              const isActive =
                (slide.textColor ??
                  (slide.type === 'pricing' ||
                  slide.type === 'two-column' ||
                  slide.type === 'transcript'
                    ? 'dark'
                    : 'light')) === c;
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => onChange({ ...slide, textColor: c } as Slide)}
                  className={`flex-1 rounded-full border px-3 py-1 font-inter text-xs ${
                    isActive
                      ? 'border-marine-green bg-marine-green text-white'
                      : 'border-beige text-slate-600 hover:border-marine-green'
                  }`}
                >
                  {c}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {(slide.type === 'right-column' ||
        slide.type === 'cards' ||
        slide.type === 'problems' ||
        slide.type === 'ask') && <AlignToggle slide={slide} onChange={onChange} />}

      {slide.type === 'cards' && <CardsPanel slide={slide} onChange={onChange} />}
      {slide.type === 'transcript' && <TranscriptPanel slide={slide} onChange={onChange} />}
      {slide.type === 'problems' && <ProblemsPanel slide={slide} onChange={onChange} />}
      {slide.type === 'pricing' && <PricingPanel slide={slide} onChange={onChange} />}
      {slide.type === 'two-column' && <TwoColumnPanel slide={slide} onChange={onChange} />}
    </div>
  );
}

function SidePanelUploadTab({ onPick }: { onPick: (path: string) => void }) {
  const [busy, setBusy] = useState(false);
  return (
    <div className="flex flex-col items-center gap-4 p-6">
      <p className="max-w-xs text-center font-inter text-xs text-slate-500">
        Upload an image from your computer. It lands in your photoslop uploads folder.
      </p>
      <label className="cursor-pointer rounded-full bg-marine-green px-5 py-2.5 font-inter text-sm font-semibold text-white hover:bg-marine-green/90">
        {busy ? 'Uploading…' : 'Choose image'}
        <input
          type="file"
          accept="image/*"
          className="hidden"
          disabled={busy}
          onChange={async (e) => {
            const f = e.target.files?.[0];
            if (!f) return;
            setBusy(true);
            try {
              const fd = new FormData();
              fd.append('file', f);
              const res = await fetch('/api/photoslop/upload', { method: 'POST', body: fd });
              if (res.ok) {
                const data = (await res.json()) as { path?: string };
                if (data.path) onPick(data.path);
              } else {
                alert(`Upload failed (${res.status})`);
              }
            } finally {
              setBusy(false);
              e.target.value = '';
            }
          }}
        />
      </label>
    </div>
  );
}

function AlignToggle({
  slide,
  onChange,
}: {
  slide: Extract<Slide, { align?: 'left' | 'right' }>;
  onChange: (s: Slide) => void;
}) {
  const current = slide.align ?? 'right';
  return (
    <div className={SECTION}>
      <p className={FIELD_LABEL}>Align content</p>
      <div className="flex gap-2">
        {(['left', 'right'] as const).map((a) => {
          const isActive = current === a;
          return (
            <button
              key={a}
              type="button"
              onClick={() => onChange({ ...slide, align: a } as Slide)}
              className={`flex-1 rounded-full border px-3 py-1 font-inter text-xs ${
                isActive
                  ? 'border-marine-green bg-marine-green text-white'
                  : 'border-beige text-slate-600 hover:border-marine-green'
              }`}
            >
              {a}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function CardsPanel({ slide, onChange }: { slide: CardsSlide; onChange: (s: Slide) => void }) {
  const updateCards = (cards: CardsSlide['cards']) => onChange({ ...slide, cards });
  return (
    <div className={SECTION}>
      <div className="flex items-center justify-between">
        <p className={FIELD_LABEL}>Cards ({slide.cards.length})</p>
        <button
          type="button"
          className={BUTTON}
          onClick={() =>
            updateCards([...slide.cards, { title: 'New card', subtitle: '', body: 'Body copy.' }])
          }
        >
          + Add
        </button>
      </div>
      <ul className="space-y-1">
        {slide.cards.map((c, i) => (
          <li
            key={i}
            className="flex items-center justify-between rounded-md bg-cream-alt px-2 py-1"
          >
            <span className="truncate font-inter text-xs text-slate-700">
              {i + 1}. {c.title || '(untitled)'}
            </span>
            <button
              type="button"
              className="font-inter text-xs text-slate-400 hover:text-red-500"
              onClick={() => updateCards(slide.cards.filter((_, j) => j !== i))}
            >
              remove
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function TranscriptPanel({
  slide,
  onChange,
}: {
  slide: TranscriptSlide;
  onChange: (s: Slide) => void;
}) {
  const updateMessages = (messages: TranscriptMessage[]) => onChange({ ...slide, messages });
  return (
    <div className={SECTION}>
      <div className="flex items-center justify-between">
        <p className={FIELD_LABEL}>Messages ({slide.messages.length})</p>
        <div className="flex gap-1">
          <button
            type="button"
            className={BUTTON}
            onClick={() =>
              updateMessages([...slide.messages, { sender: 'user', text: 'question?' }])
            }
          >
            + User
          </button>
          <button
            type="button"
            className={BUTTON}
            onClick={() =>
              updateMessages([...slide.messages, { sender: 'robin', text: 'answer.' }])
            }
          >
            + Robin
          </button>
        </div>
      </div>
      <ul className="space-y-1">
        {slide.messages.map((m, i) => (
          <li
            key={i}
            className="flex items-center justify-between gap-2 rounded-md bg-cream-alt px-2 py-1"
          >
            <select
              value={m.sender}
              onChange={(e) => {
                const next = [...slide.messages];
                next[i] = { ...m, sender: e.target.value as 'user' | 'robin' };
                updateMessages(next);
              }}
              className="rounded border border-beige bg-white px-1 py-0.5 font-dank-mono text-[10px]"
            >
              <option value="user">user</option>
              <option value="robin">robin</option>
            </select>
            <span className="flex-1 truncate font-inter text-xs text-slate-700">{m.text}</span>
            <button
              type="button"
              className="font-inter text-xs text-slate-400 hover:text-red-500"
              onClick={() => updateMessages(slide.messages.filter((_, j) => j !== i))}
            >
              remove
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ProblemsPanel({
  slide,
  onChange,
}: {
  slide: ProblemsSlide;
  onChange: (s: Slide) => void;
}) {
  const updateProblems = (problems: ProblemItem[]) => onChange({ ...slide, problems });
  return (
    <div className={SECTION}>
      <div className="flex items-center justify-between">
        <p className={FIELD_LABEL}>Problems ({slide.problems.length})</p>
        <button
          type="button"
          className={BUTTON}
          onClick={() =>
            updateProblems([
              ...slide.problems,
              {
                tag: `Problem ${slide.problems.length + 1}`,
                title: 'New problem.',
                lede: 'Lede.',
                bullets: ['Bullet.'],
              },
            ])
          }
        >
          + Add
        </button>
      </div>
      {slide.problems.map((p, i) => (
        <div key={i} className="rounded-md border border-beige bg-cream-alt p-2">
          <div className="flex items-center justify-between">
            <p className="font-inter text-xs font-semibold text-slate-700">{p.tag}</p>
            <div className="flex gap-1">
              <button
                type="button"
                className="font-inter text-[10px] text-slate-500 hover:text-marine-green"
                onClick={() => {
                  const next = [...slide.problems];
                  next[i] = { ...p, bullets: [...p.bullets, 'New bullet'] };
                  updateProblems(next);
                }}
              >
                +bullet
              </button>
              <button
                type="button"
                className="font-inter text-[10px] text-slate-400 hover:text-red-500"
                onClick={() => updateProblems(slide.problems.filter((_, j) => j !== i))}
              >
                remove
              </button>
            </div>
          </div>
          <ul className="mt-1 space-y-0.5">
            {p.bullets.map((b, bi) => (
              <li key={bi} className="flex items-center justify-between gap-2 rounded px-1 py-0.5">
                <span className="flex-1 truncate font-inter text-[11px] text-slate-600">{b}</span>
                <button
                  type="button"
                  className="font-inter text-[10px] text-slate-400 hover:text-red-500"
                  onClick={() => {
                    const next = [...slide.problems];
                    next[i] = { ...p, bullets: p.bullets.filter((_, k) => k !== bi) };
                    updateProblems(next);
                  }}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function PricingPanel({ slide, onChange }: { slide: PricingSlide; onChange: (s: Slide) => void }) {
  const updateTiers = (tiers: PricingTier[]) => onChange({ ...slide, tiers });
  return (
    <div className={SECTION}>
      <div className="flex items-center justify-between">
        <p className={FIELD_LABEL}>Tiers ({slide.tiers.length})</p>
        <button
          type="button"
          className={BUTTON}
          onClick={() =>
            updateTiers([
              ...slide.tiers,
              {
                name: 'New tier',
                price: '$0',
                priceSuffix: '',
                body: 'Body.',
                features: ['Feature.'],
              },
            ])
          }
        >
          + Add
        </button>
      </div>
      {slide.tiers.map((t, i) => (
        <div key={i} className="rounded-md border border-beige bg-cream-alt p-2">
          <div className="flex items-center justify-between">
            <p className="font-inter text-xs font-semibold text-slate-700">{t.name}</p>
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-1 font-inter text-[10px] text-slate-600">
                <input
                  type="checkbox"
                  checked={!!t.highlighted}
                  onChange={(e) => {
                    const next = [...slide.tiers];
                    next[i] = { ...t, highlighted: e.target.checked };
                    updateTiers(next);
                  }}
                />
                highlight
              </label>
              <button
                type="button"
                className="font-inter text-[10px] text-slate-500 hover:text-marine-green"
                onClick={() => {
                  const next = [...slide.tiers];
                  next[i] = { ...t, features: [...t.features, 'New feature'] };
                  updateTiers(next);
                }}
              >
                +feat
              </button>
              <button
                type="button"
                className="font-inter text-[10px] text-slate-400 hover:text-red-500"
                onClick={() => updateTiers(slide.tiers.filter((_, j) => j !== i))}
              >
                remove
              </button>
            </div>
          </div>
          <label className="mt-1 block font-dank-mono text-[9px] uppercase tracking-widest text-slate-400">
            Badge
            <input
              type="text"
              value={t.badge ?? ''}
              onChange={(e) => {
                const next = [...slide.tiers];
                next[i] = { ...t, badge: e.target.value || undefined };
                updateTiers(next);
              }}
              placeholder="Start here"
              className="mt-0.5 w-full rounded border border-beige bg-white px-1.5 py-0.5 font-inter text-[11px] text-slate-700"
            />
          </label>
          <ul className="mt-1 space-y-0.5">
            {t.features.map((f, fi) => (
              <li key={fi} className="flex items-center justify-between gap-2 rounded px-1 py-0.5">
                <span className="flex-1 truncate font-inter text-[11px] text-slate-600">{f}</span>
                <button
                  type="button"
                  className="font-inter text-[10px] text-slate-400 hover:text-red-500"
                  onClick={() => {
                    const next = [...slide.tiers];
                    next[i] = { ...t, features: t.features.filter((_, k) => k !== fi) };
                    updateTiers(next);
                  }}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function TwoColumnPanel({
  slide,
  onChange,
}: {
  slide: TwoColumnSlide;
  onChange: (s: Slide) => void;
}) {
  const updateColumns = (columns: TwoColumnSlide['columns']) => onChange({ ...slide, columns });
  return (
    <div className={SECTION}>
      <label className="flex items-center justify-between font-inter text-xs text-slate-700">
        <span className={FIELD_LABEL}>Show middle gap</span>
        <input
          type="checkbox"
          checked={slide.showMiddleGap ?? true}
          onChange={(e) => onChange({ ...slide, showMiddleGap: e.target.checked })}
        />
      </label>
      {slide.columns.map((c, i) => (
        <div key={i} className="rounded-md border border-beige bg-cream-alt p-2">
          <div className="flex items-center justify-between">
            <p className="font-inter text-xs font-semibold text-slate-700">{c.title}</p>
            <button
              type="button"
              className="font-inter text-[10px] text-slate-500 hover:text-marine-green"
              onClick={() => {
                const next = [...slide.columns];
                next[i] = { ...c, bullets: [...c.bullets, 'New bullet'] };
                updateColumns(next);
              }}
            >
              +bullet
            </button>
          </div>
          <ul className="mt-1 space-y-0.5">
            {c.bullets.map((b, bi) => (
              <li key={bi} className="flex items-center justify-between gap-2 rounded px-1 py-0.5">
                <span className="flex-1 truncate font-inter text-[11px] text-slate-600">{b}</span>
                <button
                  type="button"
                  className="font-inter text-[10px] text-slate-400 hover:text-red-500"
                  onClick={() => {
                    const next = [...slide.columns];
                    next[i] = { ...c, bullets: c.bullets.filter((_, k) => k !== bi) };
                    updateColumns(next);
                  }}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}
